/**
 * @fileoverview Form Field Component
 *
 * Renders different field types based on configuration.
 * Handles validation errors and accessibility.
 */

import type { RefObject } from "react";
import { FileUpload, type FileUploadRef } from "@/components/ui/file-upload";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { FormField as FormFieldConfig } from "@/lib/config/forms";
import { cn } from "@/lib/utils";

// Formspree limits: 25MB per file, 10 files max, 100MB total per submission
const MAX_FILES = 10;

export interface FormFieldProps {
	field: FormFieldConfig;
	value: string;
	onChange: (value: string) => void;
	onBlur: () => void;
	error?: string;
	setError?: (error: string | undefined) => void;
	/** Ref for file upload component - required for file fields */
	fileUploadRef?: RefObject<FileUploadRef | null>;
}

export function FormField({
	field,
	value,
	onChange,
	onBlur,
	error,
	setError,
	fileUploadRef,
}: FormFieldProps) {
	const fieldId = `field-${field.id}`;
	const errorId = `${fieldId}-error`;
	const helpTextId = `${fieldId}-help`;

	const commonProps = {
		id: fieldId,
		name: field.name,
		placeholder: field.placeholder,
		required: field.validation?.required,
		autoComplete: field.autoComplete,
		"aria-invalid": Boolean(error),
		"aria-describedby": cn(error && errorId, field.helpText && helpTextId),
		error: Boolean(error),
	};

	const renderField = () => {
		switch (field.type) {
			case "textarea":
				return (
					<Textarea
						{...commonProps}
						value={value}
						onChange={(e) => onChange(e.target.value)}
						onBlur={onBlur}
						rows={
							field.validation?.minLength
								? Math.ceil(field.validation.minLength / 50)
								: 4
						}
					/>
				);

			case "select":
				return (
					<Select
						{...commonProps}
						value={value}
						onChange={(e) => onChange(e.target.value)}
						onBlur={onBlur}
					>
						{field.placeholder && (
							<option value="" disabled>
								{field.placeholder}
							</option>
						)}
						{field.options?.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</Select>
				);

			case "file":
				return (
					<FileUpload
						ref={fileUploadRef}
						name={field.name}
						accept={field.accept}
						multiple={field.multiple}
						maxFiles={field.multiple ? MAX_FILES : 1}
						required={field.validation?.required}
						helpText={field.helpText}
						error={error}
						onChange={(files) => {
							// Update parent state with file count for validation tracking
							if (files.length === 0) {
								onChange("");
							} else if (files.length === 1) {
								onChange(files[0].name);
							} else {
								onChange(`${files.length} files selected`);
							}
						}}
						onError={setError}
						aria-describedby={cn(
							error && errorId,
							field.helpText && helpTextId,
						)}
					/>
				);

			case "hidden":
				return (
					<input type="hidden" id={fieldId} name={field.name} value={value} />
				);

			default:
				return (
					<Input
						{...commonProps}
						type={field.type}
						value={value}
						onChange={(e) => onChange(e.target.value)}
						onBlur={onBlur}
						pattern={field.validation?.pattern}
						minLength={field.validation?.minLength}
						maxLength={field.validation?.maxLength}
					/>
				);
		}
	};

	// Hidden fields don't need labels or error messages
	if (field.type === "hidden") {
		return renderField();
	}

	// File fields handle their own help text and errors via FileUpload component
	const isFileField = field.type === "file";

	return (
		<div className={cn("space-y-2", field.className)}>
			{field.label && (
				<Label htmlFor={fieldId}>
					{field.label}
					{field.validation?.required && (
						<span className="text-destructive ml-1">*</span>
					)}
				</Label>
			)}
			{renderField()}
			{!isFileField && field.helpText && !error && (
				<p id={helpTextId} className="text-sm text-muted-foreground">
					{field.helpText}
				</p>
			)}
			{!isFileField && error && (
				<p id={errorId} className="text-sm text-destructive" role="alert">
					{error}
				</p>
			)}
		</div>
	);
}
